import {
  useEffect,
  useRef,
} from 'react'
import {
  Container,
  Button,
} from './index'

const Scribble = () => (
  <svg
    data-testid="scribble-svg"
    aria-hidden="true"
    viewBox="0 0 418 42"
    className="absolute top-2/3 left-0 h-[0.58em] w-full fill-blue-300/70 z-0"
    preserveAspectRatio="none"
  >
    <path d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046c7.461.108 19.25 1.696 19.17 2.582-.107 1.183-7.874 4.31-25.75 10.366-21.992 7.45-35.43 12.534-36.701 13.884-2.173 2.308-.202 4.407 4.442 4.734 2.654.187 3.263.157 15.593-.78 35.401-2.686 57.944-3.488 88.365-3.143 46.327.526 75.721 2.23 130.788 7.584 19.787 1.924 20.814 1.98 24.557 1.332l.066-.011c1.201-.203 1.53-1.825.399-2.335-2.911-1.31-4.893-1.604-22.048-3.261-57.509-5.556-87.871-7.36-132.059-7.842-23.239-.254-33.617-.116-50.627.674-11.629.54-42.371 2.494-46.696 2.967-2.359.259 8.133-3.625 26.504-9.81 23.239-7.825 27.934-10.149 28.304-14.005.417-4.348-3.529-6-16.878-7.066Z"></path>
  </svg>
)

interface HeroProps {
  title: string
  subtitle: string
  primaryAction?: {
    text: string
    href?: string
    onClick?: () => void
  }
  secondaryAction?: {
    text: string
    href?: string
    onClick?: () => void
  }
  showLogos?: boolean
  logos?: Array<{
    name: string
    src: string
    alt: string
  }>
  highlightedText?: string
}

export function Hero({
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  showLogos = false,
  logos = [],
  highlightedText
}: HeroProps) {
  const _scrollTimeoutRef = useRef<number>()

  useEffect(() => {
    // Handle scroll to hash on mount
    if (window.location.hash) {
      const id = window.location.hash.slice(1)
      const element = document.getElementById(id)
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [])

  const renderTitle = () => {
    if (!highlightedText) {
      return title
    }
    const parts = title.split(highlightedText)
    if (parts.length === 1) {
      return title
    }
    return (
      <>
        {parts[0]}
        <span className="relative inline-block">
          <span className="absolute inset-x-0 top-2/3 -translate-y-1/2 flex justify-center pointer-events-none select-none">
            <Scribble />
          </span>
          <span className="relative z-10 text-blue-600">{highlightedText}</span>
        </span>
        {parts[1]}
      </>
    )
  }

  return (
    <Container className="pt-20 pb-16 text-center lg:pt-32">
      <h1 className="mx-auto max-w-4xl font-display text-5xl font-medium tracking-tight text-slate-900 sm:text-7xl">
        {renderTitle()}
      </h1>
      <p className="mx-auto mt-6 max-w-2xl text-lg tracking-tight text-slate-700">
        {subtitle}
      </p>
      {(primaryAction || secondaryAction) && (
        <div className="mt-10 flex justify-center gap-x-6">
          {primaryAction && (
            primaryAction.href?.startsWith('#') ? (
              <a
                href={primaryAction.href}
                onClick={(e) => {
                  e.preventDefault()
                  if (primaryAction.href) {
                    const href = primaryAction.href
                    window.history.pushState(null, '', href)
                    
                    const element = document.getElementById(href.slice(1))
                    if (element) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      })
                    }
                  }
                  primaryAction.onClick?.()
                }}
                className="group inline-flex items-center justify-center rounded-full py-2 px-4 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100 focus-visible:outline-blue-600"
              >
                {primaryAction.text}
              </a>
            ) : (
              <Button
                variant="solid"
                color="blue"
                to={primaryAction.href}
                onClick={primaryAction.onClick}
              >
                {primaryAction.text}
              </Button>
            )
          )}
          {secondaryAction && (
            secondaryAction.href?.startsWith('#') ? (
              <a
                href={secondaryAction.href}
                onClick={(e) => {
                  e.preventDefault()
                  if (secondaryAction.href) {
                    const element = document.getElementById(secondaryAction.href.slice(1))
                    if (element && typeof element.scrollIntoView === 'function') {
                      element.scrollIntoView({ behavior: 'smooth' })
                      window.history.pushState(null, '', secondaryAction.href)
                    }
                  }
                  secondaryAction.onClick?.()
                }}
                className="group inline-flex ring-1 items-center justify-center rounded-full py-2 px-4 text-sm ring-slate-200 text-slate-700 hover:text-slate-900 hover:ring-slate-300 active:bg-slate-100 active:text-slate-600 focus-visible:outline-blue-600 focus-visible:ring-slate-300"
              >
                {secondaryAction.text}
              </a>
            ) : (
              <Button
                variant="outline"
                color="slate"
                to={secondaryAction.href}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.text}
              </Button>
            )
          )}
        </div>
      )}
      {showLogos && logos.length > 0 && (
        <div className="mt-36 lg:mt-44">
          <p className="font-display text-base text-slate-900">
            Trusted by these companies
          </p>
          <ul className="mt-8 flex items-center justify-center gap-x-8 sm:flex-col sm:gap-x-0 sm:gap-y-10 xl:flex-row xl:gap-x-12 xl:gap-y-0">
            {logos.map((logo) => (
              <li key={logo.name} className="flex">
                <img src={logo.src} alt={logo.alt} className="h-8 w-auto" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </Container>
  )
} 